import { useTypedQuery } from 'apiTyped/helpers/useTypedQuery';
import get from 'lodash/get';
import omit from 'lodash/omit';
import type { paths } from 'apiTyped/types';

export type V2MarketSummaryRow =
  paths['/v2/distribution/pos/market-summary']['get']['responses'][200]['content']['application/json']['data'][number];

export type V2DistributionPosStatus =
  | 'all'
  | 'pos'
  | 'lost'
  | 'conquer'
  | 'other';

export type V2DistributionPosEvolutionPeriod = 'latest';

export type V2DistributionPosFilter = {
  network?: string | null;
  premium_store?: boolean;
  continent_id?: number | null;
  country_id?: number | null;
  market_id?: number[] | null;
  survey_year?: string | null;
  wine_color_id?: number | null;
  wine_type_id?: number | null;
  wine_sub_type_id?: number | null;
  wine_country_id?: number | null;
  wine_region_id?: number | null;
  wine_appelation_id?: number[] | null;
  wine_grape_variety_id?: number[] | null;
  wine_format_group_id?: number | null;
  spirit_format_group_id?: number | null;
  store_menu_price_band_id?: number[] | null;
  store_facing_band_id?: number[] | null;
  store_awards_id?: number[] | null;
  wine_format_size?: string[] | null;
  store_ws_award?: string[] | null;
  store_star?: string[] | null;
  store_worlds_best?: string[] | null;
  store_top_bar?: string[] | null;
  store_spirited_award?: string[] | null;
  store_primary_sub_type_id?: number[] | null;
  store_review_rating_id?: number[] | null;
  vintage?: string[] | null;
  selected_client_ref_ids?: number[] | null;
  selected_client_custom_ids?: number[] | null;
  selected_rival_ref_ids?: number[] | null;
  selected_rival_custom_ids?: number[] | null;
  pos_status?: V2DistributionPosStatus | null;
  period?: V2DistributionPosEvolutionPeriod | null;
  rangePrices?: [number | null, number | null] | null;
  search?: string | null;
};

export type V2DistributionPosRow = {
  network: string;
  rank: number;
  entity_type: string;
  entity_id?: number | null;
  entity_name: string;
  facing: number;
  pos_status?: 'active' | 'lost' | 'prospect' | 'unknown' | null;
  av_price?: number | null;
  displayed_currency: string;
  market_name?: string | null;
  country_id?: number | null;
  store_address?: string | null;
  store_postcode?: string | null;
  store_city_name?: string | null;
  store_phone?: string | null;
  store_url?: string | null;
  ws_award?: string | null;
  store_star?: string | null;
  store_menu_price_band_name?: string | null;
  store_primary_sub_type_name?: string | null;
  client_refs?: string | null;
  client_custom_refs?: string | null;
  rival_refs?: string | null;
  rival_custom_refs?: string | null;
};

export type V2DistributionRawFilter = Omit<
  V2DistributionPosFilter,
  | 'pos_status'
  | 'spirit_format_group_id'
  | 'store_facing_band_id'
  | 'store_awards_id'
  | 'store_review_rating_id'
> & {
  network?: string | null;
};

export type V2DistributionRawRow = {
  wine_id?: number | null;
  wine_name?: string | null;
  marque_name?: string | null;
  vintage?: string | null;
  wine_format_group_id?: number | null;
  wine_color_name?: string | null;
  store_id?: number | null;
  store_name?: string | null;
  market_name?: string | null;
  price?: number | null;
  displayed_currency: string;
  evolution_last?: string | null;
  survey_year?: string | null;
};

export type V2DistributionPosStatusSummary = {
  total: number;
  active: number;
  lost: number;
  prospect: number;
  unknown: number;
};

const toPosQueryParams = (filters: V2DistributionPosFilter) => {
  const { rangePrices, pos_status, period, ...rest } = filters;
  return {
    ...rest,
    pos_status: pos_status && pos_status !== 'all' ? pos_status : undefined,
    period: period || undefined,
    min_price: rangePrices ? rangePrices[0] : null,
    max_price: rangePrices ? rangePrices[1] : null
  };
};

export const useV2DistributionMarkets = () => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/v2/distribution/markets'
  );

  return {
    isLoadingMarkets: isLoading,
    refetch,
    marketsByContinent: get(data, 'markets_by_continent', {}) as Record<
      string,
      { id: number; name: string }[]
    >
  };
};

export const useV2DistributionMarketSummary = (
  filters: V2DistributionPosFilter,
  enabled = true
) => {
  const scope = omit(filters, ['pos_status', 'search']);
  const { data, isLoading, isFetching, isPlaceholderData, isError } =
    useTypedQuery('get', '/v2/distribution/pos/market-summary', {
      params: {
        query: {
          ...toPosQueryParams(scope),
          continent_id:
            scope.continent_id == null ? null : [scope.continent_id],
          country_id: scope.country_id == null ? null : [scope.country_id]
        }
      },
      queryOpts: { enabled }
    });
  return {
    data: data?.data ?? [],
    isLoading: isLoading || (isFetching && isPlaceholderData),
    isError
  };
};

export const useV2DistributionGeoFilters = (scope?: {
  network?: 'on' | 'off';
  premium_store?: boolean;
}) => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/v2/distribution/geo-filters',
    {
      params: scope ? { query: scope } : undefined,
      queryOpts: { placeholderData: undefined, staleTime: 10 * 60 * 1000 }
    }
  );

  return {
    isLoadingGeoFilters: isLoading,
    refetch,
    continents: get(data, 'continents', []) as { id: number; name: string }[],
    countries: get(data, 'countries', []) as {
      id: number;
      name: string;
      continent_id: number;
    }[],
    surveyYears: get(data, 'survey_years', []) as string[]
  };
};

export const useV2DistributionPos = (
  filters: V2DistributionPosFilter,
  enabled = true
) => {
  const { isLoading, isFetching, isPlaceholderData, data, refetch } =
    useTypedQuery('get', '/v2/distribution/pos', {
      params: { query: toPosQueryParams(filters) },
      queryOpts: { enabled }
    });

  return {
    isLoading,
    isFetching,
    isPlaceholderData,
    refetch,
    data: get(data, 'data', []) as V2DistributionPosRow[]
  };
};

export const useV2DistributionPosCount = (
  filters: V2DistributionPosFilter,
  enabled = true
) => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/v2/distribution/pos/count',
    {
      params: { query: toPosQueryParams(filters) },
      queryOpts: { enabled }
    }
  );

  return {
    isLoading,
    refetch,
    posCount: get(data, 'count', 0) as number
  };
};

export const useV2DistributionPosStatusSummary = (
  filters: V2DistributionPosFilter,
  enabled = true
) => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/v2/distribution/pos/status-summary',
    {
      params: { query: toPosQueryParams({ ...filters, pos_status: null }) },
      queryOpts: { enabled }
    }
  );

  return {
    isLoading,
    refetch,
    summary: (data || {
      total: 0,
      active: 0,
      lost: 0,
      prospect: 0,
      unknown: 0
    }) as V2DistributionPosStatusSummary
  };
};

export const useV2DistributionRaw = (
  filters: V2DistributionRawFilter,
  enabled = true
) => {
  const { isLoading, isFetching, isPlaceholderData, data, refetch } =
    useTypedQuery('get', '/v2/distribution/raw', {
      params: { query: toPosQueryParams(filters) },
      queryOpts: { enabled }
    });

  return {
    isLoading,
    isFetching,
    isPlaceholderData,
    refetch,
    data: get(data, 'data', []) as V2DistributionRawRow[]
  };
};
